import React from 'react';
import { Plus } from 'lucide-react';
import type { FlatMaterialRow } from '../../Perfil/PerfilMain/MisMaterialesComercio/commerceMaterialsModel.ts';
import CommerceMaterialsCatalog from '../../Perfil/PerfilMain/MisMaterialesComercio/CommerceMaterialsCatalog.tsx';
import { ReactComponent as NoDataEmptyIllustration } from '../../../assets/svg/no-data-empty-state.svg';
import styles from '../CommerceSheetReviewStep.module.scss';

export interface CommerceSheetManualCatalogSectionProps {
  displayError: string;
  addModalOpen: boolean;
  manualCatalogFlatRows: FlatMaterialRow[];
  manualCatalogTableEnabled: boolean;
  onCommerceCatalogEditItem?: (
    itemId: number,
    patch: {
      price: number | null;
      unquoted: boolean;
      valueAddedTax: boolean;
      promotion: string;
      promotionDiscount: number | null;
    }
  ) => Promise<void>;
  onCommerceCatalogDeleteItem?: (itemId: number) => Promise<void>;
  commerceCatalogActionsBusy: boolean;
  addModalSubmitting: boolean;
  openAddMaterialModal: () => void;
}

export const CommerceSheetManualCatalogSection: React.FC<CommerceSheetManualCatalogSectionProps> = ({
  displayError,
  addModalOpen,
  manualCatalogFlatRows,
  manualCatalogTableEnabled,
  onCommerceCatalogEditItem,
  onCommerceCatalogDeleteItem,
  commerceCatalogActionsBusy,
  addModalSubmitting,
  openAddMaterialModal,
}) => {
  return (
    <>
      <h2 className={styles.title}>Cargar materiales a mano</h2>
      <p className={styles.intro}>
        El formulario se abre para que cargues cada material; cada material quedará guardado en tu comercio. Si cerrás el
        formulario, podés volver a abrirlo desde esta pantalla. Para usar un archivo, regresá a la pantalla anterior.
      </p>
      <div className={styles.manualCommercePanel}>
        <div className={styles.manualCommercePanelToolbar}>
          <div className={styles.manualCommercePanelToolbarText}>
            <h3 className={styles.manualCommercePanelTitle}>Tu catálogo de materiales</h3>
          </div>
          {!addModalOpen && manualCatalogFlatRows.length > 0 ? (
            <div className={styles.manualCommercePanelToolbarActions}>
              <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
                <Plus size={16} />
                Agregar material al catálogo
              </button>
            </div>
          ) : null}
        </div>
        {manualCatalogFlatRows.length === 0 ? (
          <p className={styles.manualCommercePanelEmpty}>Todavía no hay materiales en tu listado.</p>
        ) : manualCatalogTableEnabled ? (
          <div className={styles.manualCommerceCatalogWrap}>
            <CommerceMaterialsCatalog
              mode="owner"
              flatRows={manualCatalogFlatRows}
              onEditItem={onCommerceCatalogEditItem}
              onDeleteItem={onCommerceCatalogDeleteItem}
              disableActions={commerceCatalogActionsBusy || addModalSubmitting}
            />
          </div>
        ) : (
          <ul className={styles.manualCommerceList} aria-label="Materiales ya listados">
            {manualCatalogFlatRows.map((r) => (
              <li key={`${r.categoryId}-${r.material.id}`} className={styles.manualCommerceListItem}>
                <span className={styles.manualCommerceListName}>{r.material.name}</span>
                <span className={styles.manualCommerceListMeta}>
                  {r.categoryName}
                  {r.material.unit ? ` · ${r.material.unit}` : ''}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {displayError ? <p className={styles.error}>{displayError}</p> : null}
      {!addModalOpen && manualCatalogFlatRows.length === 0 ? (
        <div className={styles.manualEmptyState}>
          <NoDataEmptyIllustration className={styles.manualEmptyIllustration} aria-hidden />
          <div className={styles.manualAltaReopen}>
            <button type="button" className={styles.addMaterialButton} onClick={openAddMaterialModal}>
              <Plus size={16} />
              Agregar material al catálogo
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
};
